import {
  BaseApiService,
  type JsonApiResponse,
  type ServiceOptions,
} from './base.service';
import type {
  ProjectReference,
  ProjectReferenceStoreResponse,
  ProjectReferenceCategory,
  ProjectReferenceAspect,
  ProjectReferenceSection,
} from '@/types/project-reference';

export type ProjectReferenceWarningTarget = 'project_goals' | 'project_angles';

export interface ProjectReferenceWarning {
  type: string;
  message: string;
  link: {
    text: string;
    target: ProjectReferenceWarningTarget;
  };
}

export interface ProjectReferencesListResult {
  references: ProjectReference[];
  warnings: ProjectReferenceWarning[];
}

export class ProjectReferencesService extends BaseApiService {
  /**
   * Get all references for a project, optionally filtered to one intake
   * section (`brief` = current state, `reference` = inspiration). Omit
   * `section` to list every section.
   */
  async listProjectReferences(
    projectId: string,
    section?: ProjectReferenceSection,
    options?: ServiceOptions
  ): Promise<ProjectReferencesListResult> {
    const queryString = section
      ? `?${this.buildQueryString({ section })}`
      : '';
    const response = await this.httpGet<JsonApiResponse<ProjectReference[]>>(
      `/v1/projects/${projectId}/references${queryString}`,
      undefined,
      {
        loading: { trackEndpoint: `project-references-list-${projectId}` },
        ...options,
      }
    );
    return {
      references: response.data,
      warnings: (response.meta?.warnings as ProjectReferenceWarning[]) ?? [],
    };
  }

  /**
   * Get a single project reference
   */
  async getProjectReference(
    projectId: string,
    referenceId: string,
    options?: ServiceOptions
  ): Promise<ProjectReference> {
    const response = await this.httpGet<JsonApiResponse<ProjectReference>>(
      `/v1/projects/${projectId}/references/${referenceId}`,
      undefined,
      {
        loading: { trackEndpoint: `project-reference-get-${referenceId}` },
        ...options,
      }
    );
    return response.data;
  }

  /**
   * Create a new project reference (returns upload policy)
   */
  async createProjectReference(
    projectId: string,
    data: {
      md5: string;
      fileName?: string;
      contentType: string;
      fileSize: number;
      pageCount?: number; // For PDFs
      width?: number; // Post-downscale pixel width (image references only)
      height?: number; // Post-downscale pixel height (image references only)
      // Intake section the upload belongs to (brief = current state,
      // reference = inspiration). Omit to let the API default to `reference`.
      section?: ProjectReferenceSection;
      // Pre-seeded aspect(s) for inline upload from a render reference
      // sub-panel (Color panel → ['color']). The server persists these on
      // create and the classifier MERGES its result on top (superset), so the
      // panel's aspect is guaranteed present. Omit on every other upload path.
      aspects?: ProjectReferenceAspect[];
      // Output-style role for inline upload from the render Output sub-panel
      // (`'output'`). Mutually exclusive with `aspects` — output is a render-time
      // role, not an aspect. The server keeps the output role and sets an
      // output-style usage description. Omit on every other upload path.
      category?: ProjectReferenceCategory;
    },
    options?: ServiceOptions
  ): Promise<ProjectReferenceStoreResponse> {
    const requestData = this.formatJsonApiData('project_reference', {
      md5: data.md5,
      file_name: data.fileName,
      content_type: data.contentType,
      file_size: data.fileSize,
      ...(data.section && { section: data.section }),
      ...(data.aspects && data.aspects.length > 0 && { aspects: data.aspects }),
      ...(data.category && { category: data.category }),
      ...(data.pageCount && { page_count: data.pageCount }),
      // Image references carry their final (post-downscale) dimensions;
      // documents (PDF/txt/md) have no pixel size, so omit when absent.
      ...(data.width && data.height
        ? { width: data.width, height: data.height }
        : {}),
    });

    const response = await this.post<
      JsonApiResponse<ProjectReferenceStoreResponse>
    >(`/v1/projects/${projectId}/references`, requestData, undefined, {
      loading: { trackEndpoint: 'project-reference-create' },
      ...options,
    });
    return response.data;
  }

  /**
   * Confirm upload completed - triggers classification
   */
  async confirmUpload(
    projectId: string,
    referenceId: string,
    options?: ServiceOptions
  ): Promise<ProjectReference> {
    const response = await this.post<JsonApiResponse<ProjectReference>>(
      `/v1/projects/${projectId}/references/${referenceId}/confirm`,
      undefined,
      undefined,
      {
        loading: { trackEndpoint: `project-reference-confirm-${referenceId}` },
        ...options,
      }
    );
    return response.data;
  }

  /**
   * Update reference category (user manual override)
   */
  async updateCategory(
    projectId: string,
    referenceId: string,
    category: ProjectReferenceCategory,
    options?: ServiceOptions
  ): Promise<ProjectReference> {
    return this.updateReference(projectId, referenceId, { category }, options);
  }

  /**
   * Re-home a reference between intake sections (`brief` = current state,
   * `reference` = inspiration). Fixes a wrong-section upload by flipping
   * `data.attributes.section` on the same PATCH endpoint.
   */
  async updateSection(
    projectId: string,
    referenceId: string,
    section: ProjectReferenceSection,
    options?: ServiceOptions
  ): Promise<ProjectReference> {
    return this.updateReference(projectId, referenceId, { section }, options);
  }

  /**
   * Update reference attributes (category, description, section)
   */
  async updateReference(
    projectId: string,
    referenceId: string,
    attrs: {
      category?: ProjectReferenceCategory;
      aspects?: ProjectReferenceAspect[];
      description?: string | null;
      section?: ProjectReferenceSection;
    },
    options?: ServiceOptions
  ): Promise<ProjectReference> {
    const requestData = this.formatJsonApiData(
      'project_reference',
      attrs,
      undefined,
      referenceId
    );

    const response = await this.patch<JsonApiResponse<ProjectReference>>(
      `/v1/projects/${projectId}/references/${referenceId}`,
      requestData,
      undefined,
      {
        loading: { trackEndpoint: `project-reference-update-${referenceId}` },
        ...options,
      }
    );
    return response.data;
  }

  /**
   * Delete a project reference (soft delete)
   */
  async deleteProjectReference(
    projectId: string,
    referenceId: string,
    options?: ServiceOptions
  ): Promise<void> {
    await this.httpDelete(
      `/v1/projects/${projectId}/references/${referenceId}`,
      undefined,
      {
        loading: { trackEndpoint: `project-reference-delete-${referenceId}` },
        ...options,
      }
    );
  }

  async restoreProjectReference(
    projectId: string,
    referenceId: string
  ): Promise<void> {
    await this.post(
      `/v1/projects/${projectId}/references/${referenceId}/restore`
    );
  }

  /**
   * Get references by category
   */
  async getProjectReferencesByCategory(
    projectId: string,
    category: ProjectReferenceCategory,
    options?: ServiceOptions
  ): Promise<ProjectReference[]> {
    const queryString = this.buildQueryString({
      filter: { category },
    });

    const response = await this.httpGet<JsonApiResponse<ProjectReference[]>>(
      `/v1/projects/${projectId}/references?${queryString}`,
      undefined,
      {
        loading: { trackEndpoint: `project-references-category-${category}` },
        ...options,
      }
    );
    return response.data;
  }

  /**
   * Get only image references (for render settings)
   */
  async getImageReferences(
    projectId: string,
    options?: ServiceOptions
  ): Promise<ProjectReference[]> {
    const queryString = this.buildQueryString({
      filter: { type: 'image' },
    });

    const response = await this.httpGet<JsonApiResponse<ProjectReference[]>>(
      `/v1/projects/${projectId}/references?${queryString}`,
      undefined,
      {
        loading: { trackEndpoint: 'project-references-images' },
        ...options,
      }
    );
    return response.data;
  }

  /**
   * Get only document references (for LLM context)
   */
  async getDocumentReferences(
    projectId: string,
    options?: ServiceOptions
  ): Promise<ProjectReference[]> {
    const queryString = this.buildQueryString({
      filter: { category: 'data' },
    });

    const response = await this.httpGet<JsonApiResponse<ProjectReference[]>>(
      `/v1/projects/${projectId}/references?${queryString}`,
      undefined,
      {
        loading: { trackEndpoint: 'project-references-documents' },
        ...options,
      }
    );
    return response.data;
  }
}

export const projectReferencesService = new ProjectReferencesService();
